<template>
  <div class="d-flex flex-column fill-height">
    <div class="vacancy-questions__header-block">
      <div v-if="transcriptions.length && account.transcription" @click="goToReview" class="link">
        {{ $t('viewSummary') }}
      </div>

      <div v-if="!areQuestionsEqual">
        <Tooltip :position="'left'" :position-mob="'right'" :max-width="300">
          <template #trigger>
            <VIcon>mdi-help-circle</VIcon>
          </template>
          <template #content>
            {{ $t('questionsChanged') }}
          </template>
        </Tooltip>
      </div>
    </div>

    <VList
      rounded
      class="vacancy-questions__list pa-2 pa-md-4 flex-grow-1"
      :class="{'huntica-nice-scroll': $vuetify.breakpoint.smAndUp}"
    >
      <VListItemGroup v-model="currentQuestion" mandatory>
        <VListItem
          v-for="(q, index) in filteredOnlineInterviewTimestamps"
          :key="`${q.type}-${q.id}`"
          active-class="item-active"
          class="vacancy-questions__item"
          :class="{ 'pre-active': currentQuestion - 1 === index }"
        >
          <div class="vacancy-questions__item_header">
            <VListItemIcon class="vacancy-questions__icon mr-2">
              <VIcon
                :color="'primary'"
                @click="togglePlayPause(index)"
                :disabled="isDisabledQuestion(q.id, q.type)"
              >
                <template v-if="currentQuestion === index && isQuestionPlaying">
                  mdi-pause
                </template>
                <template v-else>
                  mdi-play
                </template>
              </VIcon>
            </VListItemIcon>

            <VListItemIcon
              v-if="shouldShowTranscriptionIcon"
              class="vacancy-questions__icon mr-3"
            >
              <VIcon @click="toggleTxtIcon(index)">
                <template v-if="isTxtTextIconsClicked[index]">
                  mdi-arrow-up
                </template>
                <template v-else>
                  TXT
                </template>
              </VIcon>
            </VListItemIcon>

            <VListItemContent class="body-2">
              <div class="d-flex justify-start">
                <span class="num pr-3">{{ index + 1 }}.</span>
                <span class="text">{{ q.question }}</span>
              </div>
            </VListItemContent>
          </div>

          <div v-if="isTxtTextIconsClicked[index]" class="vacancy-questions__item_content">
            <template v-if="getTranscriptionByQuestionId(q.id)">
              <div class="vacancy-questions__item_content-text" :class="{ maxHeight: isReadFull }">
                {{ getTranscriptionByQuestionId(q.id).transcription }}
              </div>
              <div
                v-if="whichQestionLength(getTranscriptionByQuestionId(q.id).transcription) >= 190"
                @click="readFull"
                class="read-full"
              >
                <template v-if="!isReadFull">
                  {{ $t('readFull') }}
                </template>
                <template v-else>
                  {{ $t('hide') }}
                </template>
              </div>
            </template>

            <template v-else-if="!account.transcription">
              <VCard class="pa-3 error__trial">
                <VRow class="flex-column">
                  <svg
                    class="error__trial_icon"
                    width="20"
                    height="19"
                    viewBox="0 0 20 19"
                  >
                    <path
                      d="M19.2331 12.775L12.4998 2.14168C11.9331 1.32501 10.9915 0.833344 9.9998 0.833344C9.00813 0.833344 8.06646 1.31668 7.4748 2.16668L0.774796 12.7583C-0.0752044 13.975 -0.233538 15.4333 0.358129 16.5583C0.941462 17.6833 2.16646 18.325 3.6998 18.325H16.2998C17.8415 18.325 19.0581 17.6833 19.6415 16.5583C20.2248 15.4333 20.0665 13.9833 19.2331 12.775ZM9.16646 5.83334C9.16646 5.37501 9.54146 5.00001 9.9998 5.00001C10.4581 5.00001 10.8331 5.37501 10.8331 5.83334V10.8333C10.8331 11.2917 10.4581 11.6667 9.9998 11.6667C9.54146 11.6667 9.16646 11.2917 9.16646 10.8333V5.83334ZM9.9998 15.8333C9.30813 15.8333 8.7498 15.275 8.7498 14.5833C8.7498 13.8917 9.30813 13.3333 9.9998 13.3333C10.6915 13.3333 11.2498 13.8917 11.2498 14.5833C11.2498 15.275 10.6915 15.8333 9.9998 15.8333Z"
                      fill="#F94141"
                    />
                  </svg>

                  <VCol class="pa-0 pb-2" cols="12">
                    <div class="error__title">
                      <template v-if="isExpired">
                        {{ $t('isExpiredTrialTitle') }}
                      </template>
                      <template v-else>
                        {{ $t('conversionIsAvailable') }}
                      </template>
                    </div>
                  </VCol>

                  <VCol cols="12" class="error__trial_links pa-0">
                    <div v-if="account.trialAvailable && !isExpired" class="text-end try-days-block">
                      <RouterLink
                        class="button-trial"
                        :to="{ name: 'settings', params: { type: settingsTabs.PAYMENT, addType: paymentTabs.PLANS } }"
                      >
                        {{ $t('free') }}
                      </RouterLink>
                    </div>

                    <div class="text-center change-tariff-block">
                      <RouterLink
                        class="button-tariff"
                        :to="isExpired ? { name: 'settings', params: { type: settingsTabs.PAYMENT, addType: paymentTabs.INFO } } : { name: 'settings', params: { type: settingsTabs.PAYMENT, addType: paymentTabs.PLANS } }"
                      >
                        <span v-if="isExpired">{{ $t('payTheTariff') }}</span>
                        <span v-else>{{ $t('changeTariff') }}</span>
                      </RouterLink>
                    </div>
                  </VCol>
                </VRow>
              </VCard>
            </template>
          </div>
        </VListItem>
      </VListItemGroup>
    </VList>

    <CandidateLogCreateForm
      :candidate="candidate"
      :interview-type="interviewType"
      :office-interview-player-time="officeInterviewPlayerTime"
      :online-interview-question-index="activeQuestion"
      @change-active-tab="$emit('change-active-tab', $event)"
      class="candidate-log-create-form elevation-5 pa-2 pa-md-5"
    />
  </div>
</template>

<script>
import useAccount from '@/compositions/accounting/useAccount';
import useSubscription from '@/compositions/accounting/useSubscription';
import { paymentTabs, settingsTabs, timestampTypes } from '@/enums';
import {
  computed, ref, toRefs,
} from '@vue/composition-api';
import CandidateLogCreateForm from '@/forms/CandidateLogCreateForm.vue';
import { mapGetters } from 'vuex';
import Tooltip from '../common/Tooltip.vue';

export default {
  name: 'VacancyQuestionsList',
  components: { CandidateLogCreateForm, Tooltip },
  model: {
    prop: 'question',
    event: 'input',
  },
  props: {
    vacancy: {
      type: Object,
      required: true,
    },
    question: {
      type: Number,
      default: 0,
    },
    onlineInterview: {
      type: Object,
      required: true,
    },
    candidate: {
      type: Object,
      required: true,
    },
    areQuestionsEqual: {
      type: Boolean,
      required: true,
      default: true,
    },
  },
  setup(props, { emit }) {
    const { onlineInterview, areQuestionsEqual, vacancy } = toRefs(props);
    const { account } = useAccount();
    const { isExpired } = useSubscription();
    const isReadFull = ref(false);

    const transcriptions = computed(() => {
      return onlineInterview.value?.timestamps.filter((i) => {
        return i.transcription;
      });
    });

    const filteredOnlineInterviewTimestamps = computed(() => {
      if (areQuestionsEqual.value) {
        return vacancy.value.script.questions;
      }

      return onlineInterview.value?.timestamps
        .filter((t) => t.type === timestampTypes.ANSWERING)
        .map((t) => ({
          id: t.questionId,
          questionId: null,
          type: t.questionType,
          file: null,
          question: t.questionText,
          interviewerId: null,
          duration: null,
          startTime: t.startTime,
          speechStopTime: t.stopTime,
          stopTime: null,
          uuid: null,
        }));
    });

    const shouldShowTranscriptionIcon = computed(() => {
      return transcriptions.value.length > 0 || isExpired.value || !account.value.transcription;
    });

    function goToReview() {
      emit('change-active-tab', 0);
    }

    function readFull() {
      isReadFull.value = !isReadFull.value;
    }

    function whichQestionLength(text) {
      return text.split(' ').join('').length;
    }

    function getTranscriptionByQuestionId(questionId) {
      return transcriptions.value.find((t) => t.questionId === questionId && t.transcription);
    }

    return {
      account,
      paymentTabs,
      settingsTabs,
      isReadFull,
      isExpired,
      readFull,
      transcriptions,
      whichQestionLength,
      goToReview,
      getTranscriptionByQuestionId,
      filteredOnlineInterviewTimestamps,
      shouldShowTranscriptionIcon,
    };
  },
  data() {
    return {
      isQuestionPlaying: false,
      isTxtTextIcon: false,
      isTxtTextIconsClicked: [],
    };
  },
  computed: {
    toggleVideoPlayerEvent() {
      return new CustomEvent('toggleVideoPlayerPlayPause');
    },
    ...mapGetters('candidatePage', {
      interviewType: 'activeInterviewType',
      officeInterviewPlayerTime: 'officeInterviewPlayerTime',
      activeQuestion: 'activeQuestion',
    }),
    currentQuestion: {
      get() {
        return this.question;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
  },
  mounted() {
    document.addEventListener('toggleQuestionListPlayPause', this.toggleQuestionListPlayPause);
  },
  beforeDestroy() {
    document.removeEventListener('toggleQuestionListPlayPause', this.toggleQuestionListPlayPause);
  },
  methods: {
    isDisabledQuestion(id, type) {
      return !this.onlineInterview.timestamps?.some(
        (t) => t.questionId === id && t.questionType === type);
    },
    togglePlayPause(index) {
      if (index !== this.currentQuestion && this.isQuestionPlaying) {
        return;
      }
      this.toggleQuestionListPlayPause();
      this.toggleVideoPlayerPlayPause();
    },
    togglePlayFromOutside(question) {
      if (question !== this.currentQuestion && this.isQuestionPlaying) {
        return;
      }

      this.toggleQuestionListPlayPause();
      this.toggleVideoPlayerPlayPause();
    },
    toggleQuestionListPlayPause() {
      this.isQuestionPlaying = !this.isQuestionPlaying;
    },
    toggleVideoPlayerPlayPause() {
      document.dispatchEvent(this.toggleVideoPlayerEvent);
    },
    toggleTxtIcon(index) {
      this.$set(this.isTxtTextIconsClicked, index, !this.isTxtTextIconsClicked[index]);
    },
  },
};
</script>

<style lang="scss" scoped>
.vacancy-questions {
  &__list {
    background-color: transparent;
    @media #{map-get($display-breakpoints, 'md-and-up')} {
      height: 0;
    }
  }

  &__header-block {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 20px 20px 0;
    @media screen and (max-width: 768px) {
      flex-direction: column;
      align-items: flex-start;
      gap: 10px;
      margin: 10px 20px 10px;
    }
  }

  &__item {
    background: #fff;
    border-radius: 10px !important;
    margin-bottom: 5px !important;
    position: relative;
    display: block;
    &_header {
      display: flex;
      align-items: center;
      > div:nth-child(2) {
      display: block;
        button {
          color: #000 !important;
          text-align: center;
          font-size: 10px;
          font-weight: 400;
          line-height: 100%;
          padding: 7px 3px;
        }
      }
    }

    &_content {
      padding-bottom: 20px;
    }
    &_content-text {
      max-height: 60px;
      overflow-y: hidden;
      color: #212121;
      font-size: 14px;
      font-weight: 300;
      line-height: 150%;
    }

    .num {
      font-size: 15px;
    }
  }

  &__icon {
    cursor: pointer;
    border: 1px solid #E0E0E0;
    border-radius: 50%;
    button {
      width: 26px;
      height: 26px;
    }
    &:nth-child(2) button{
      font-size: 10px;
    }
  }
}
.summary {
  color: #0A82D9;
  font-size: 14px;
  font-weight: 400;
  line-height: 100%;
  text-decoration: underline;
}

.heigth {
  height: 300px;
}

.maxHeight {
  max-height: 100% !important;
  transition: .2s;
}

.read-full {
  bottom: 15px;
  width: 100%;
  text-align: center;
  color: #0A82D9;
  font-size: 14px;
  font-weight: 400;
  line-height: 100%;
  text-decoration-line: underline;
  cursor: pointer;
  margin-top: 20px;
}

.v-icon.v-icon::after {
  display: none;
}

.v-sheet.v-card:not(.v-sheet--outlined) {
  box-shadow: none;
}
.v-card::v-deep {
  background-color: #F5F5F5;
  border-radius: 10px;
}
.v-list-item::after {
  display: none;
}
.v-list-item__subtitle {
  flex: auto;
  overflow: inherit;
}

.error {
  &__title {
    color: #212121;
    font-size: 14px;
    font-weight: 400;
    line-height: normal;
  }
  &__trial {
    background-color: #FFECEC;
    padding-left: 68px !important;
    @media screen and (max-width: 600px) {
      padding: 20px 40px !important;
    }
    &_icon {
      position: absolute;
      top: 10px;
      left: 10px;
      @media screen and (max-width: 600px) {
        position: static;
        margin-bottom: 15px;
      }
    }
    &_links {
      display: flex;
      align-items: center;
      @media screen and (max-width: 1265px) {
        margin-top: 10px;
      }
      @media screen and (max-width: 600px) {
        justify-content: space-between;
        margin-top: 20px;
      }
    }
    .row {
      display: flex;
      .try-days-block {
        margin-right: 25px;
      }
      .change-tariff-block {
        white-space: nowrap;
      }
    }

    .button-trial {
      color: #0A82D9;
      font-size: 14px;
      font-weight: 400;
      line-height: normal;
      text-decoration-line: underline;
      white-space: nowrap;
    }

    .button-tariff {
      color: #212121;
      font-size: 14px;
      font-weight: 400;
      line-height: normal;
      text-decoration-line: underline;
      white-space: nowrap;
    }
  }
}
.v-list-item--link:before {
  display: none;
}

.link {
  font-size: 13px;
  color: #0a82d9;
  text-decoration: underline;
  cursor: pointer;
}

.item-active {
  border: 1px solid #1F85D7;
}
</style>

<i18n lang="yml" locale="en">
  conversionIsAvailable: Conversion is available at the "Standard" tariff and above
  free: 30 days for free
  changeTariff: Change your tariff
  payTheTariff: Pay the tariff
  isExpiredTrialTitle: Your tariff is inactive.
  isExpiredTrialText: To gain access to all the functions of your tariff, you need to make a payment.
  inProcessing: Still in processing!
  readFull: Read completely
  summaryAvailable: A brief transcript of the interview from AI is available
  hide: Hide
  noAnswer: No answer
  viewSummary: View the interview summary from AI
  questionsChanged: The job questions are different from the questions the candidate answered.
</i18n>

<i18n lang="yml" locale="ru">
  conversionIsAvailable: Конвертация доступна на тарифе «Стандарт» и выше
  free: Бесплатно 30 дн.
  changeTariff: Сменить тариф
  payTheTariff: Оплатить тариф
  isExpiredTrialTitle: Тариф закончился
  isExpiredTrialText: Для получения доступа к функциям Вашего тарифа необходимо внести оплату.
  inProcessing: Еще в обработке!
  readFull: Читать полностью
  summaryAvailable: Вам доступно краткое содержание собеседования
  hide: Скрыть
  noAnswer: Нет ответа
  viewSummary: Посмотреть резюме собеседования от ИИ
  questionsChanged: Вопросы вакансии отличаются от вопросов, на которые отвечал кандидат.
</i18n>
